import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import ProductsSection from './ProductsSection';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick, trackAction } from '../config/gtm';

const Home: React.FC = () => {
    const navigate = useNavigate();
    const [currentImage, setCurrentImage] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { language } = useLanguage();

    const t = (es: string, en: string) => language === 'es' ? es : en;

    // Imágenes del carrusel con las portadas de los libros
    const carouselImages = [
        {
            src: '/images/portadas/portadalibro1.png',
            alt: t('El Traje Nuevo del Rey', "The King's New Clothes"),
            title: t('El Traje Nuevo del Rey', "The King's New Clothes")
        },
        {
            src: '/images/portadas/portadalibro2.png',
            alt: t('La Liebre y la Tortuga', 'The Hare and the Tortoise'),
            title: t('La Liebre y la Tortuga', 'The Hare and the Tortoise')
        },
        {
            src: '/images/portadas/portadalibro3.png',
            alt: t('El Ratón de Campo y el Ratón de Ciudad', 'The Town Mouse and the Country Mouse'),
            title: t('El Ratón de Campo y el Ratón de Ciudad', 'The Town Mouse and the Country Mouse')
        },
        {
            src: '/images/portadas/portadalibro4.png',
            alt: t('El Patito Feo', 'The Ugly Duckling'),
            title: t('El Patito Feo', 'The Ugly Duckling')
        },
        {
            src: '/images/portadas/portadalibro5.png',
            alt: t('Sopa de Piedra', 'Stone Soup'),
            title: t('Sopa de Piedra', 'Stone Soup')
        },
        {
            src: '/images/portadas/portadalibro6.png',
            alt: t('Los Tres Cerditos', 'The Three Little Pigs'),
            title: t('Los Tres Cerditos', 'The Three Little Pigs')
        },
        {
            src: '/images/portadas/portadalibro7.png',
            alt: t('Hansel y Gretel', 'Hansel and Gretel'),
            title: t('Hansel y Gretel', 'Hansel and Gretel')
        },
        {
            src: '/images/portadas/portadalibro8.png',
            alt: t('La Gallina de los Huevos de Oro', 'The Goose That Laid the Golden Eggs'),
            title: t('La Gallina de los Huevos de Oro', 'The Goose That Laid the Golden Eggs')
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImage((prev) => (prev + 1) % carouselImages.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [carouselImages.length]);



    const handleThemeChange = (theme: number) => {
        // Tracking con GTM
        const bookNames = {
            2: 'El Traje Nuevo del Rey',
            3: 'La Liebre y la Tortuga',
            4: 'El Ratón de Campo y el Ratón de Ciudad',
            5: 'El Patito Feo',
            6: 'Sopa de Piedra',
            7: 'Los Tres Cerditos',
            8: 'Hansel y Gretel',
            9: 'La Gallina de los Huevos de Oro'
        };
        
        const bookName = bookNames[theme as keyof typeof bookNames] || 'Unknown Book';
        trackAction('book_selection', 'navigation', bookName);
        
        // Navegar a la página del libro correspondiente
        const bookRoutes = {
            2: '/libros/1',
            3: '/libros/2', 
            4: '/libros/3',
            5: '/libros/4',
            6: '/libros/5',
            7: '/libros/6',
            8: '/libros/7',
            9: '/libros/8'
        };
        const route = bookRoutes[theme as keyof typeof bookRoutes];
        if (route) {
            window.location.href = route;
        }
    };

    return (
        <div className="min-h-screen bg-white w-full overflow-x-hidden">
            {/* Navbar */}
            <Navbar currentTheme={1} onThemeChange={handleThemeChange} />
            {/* Contenido principal */}
            <main>
                {/* Hero Section */}
                <section 
                    className="relative min-h-screen flex items-center justify-center overflow-hidden"
                    style={{
                        background: `url('/images/telonbk.png')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                    }}
                >
                    {/* Overlay con patrón de puntos */}
                    <div 
                        className="absolute inset-0 opacity-30"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.1\"%3E%3Ccircle cx=\"30\" cy=\"30\" r=\"2\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}
                    ></div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                            {/* Columna izquierda: Texto */}
                            <div className="text-white relative z-10">
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg">
                                    {t('Transforma el Futuro de tus Hijos', "Transform Your Children's Future")}
                                </h1>
                                <p className="text-lg sm:text-xl mb-8 leading-relaxed opacity-90 drop-shadow-md">
                                    {t(
                                        'Princonser Kids es la plataforma educativa que conecta padres, educadores y niños para crear experiencias de aprendizaje únicas y originales.',
                                        'Princonser Kids is the educational platform that connects parents, educators and children to create unique and original learning experiences.'
                                    )}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button 
                                        onClick={() => {
                                            trackCTAClick('Comenzar Ahora', 'Hero Section', 'button');
                                            navigate('/login');
                                        }}
                                        className="px-8 py-4 bg-red-500 text-white rounded-full text-lg font-bold hover:bg-red-600 transition-all duration-300 transform hover:scale-105 shadow-lg border-3 border-white"
                                    >
                                        {t('🚀 Comenzar Ahora', '🚀 Start Now')}
                                    </button>
                                </div>
                            </div>
                            {/* Columna derecha: Carrusel */}
                            <div className="flex justify-center relative z-10">
                                <div className="relative w-80 h-80 sm:w-96 sm:h-96 md:w-[400px] md:h-[400px] lg:w-[450px] lg:h-[450px] xl:w-[500px] xl:h-[500px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-transparent max-w-full">
                                    {/* Imagen actual */}
                                    <div className="w-full h-full flex items-center justify-center p-4 sm:p-6">
                                        <img
                                            src={carouselImages[currentImage].src}
                                            alt={carouselImages[currentImage].alt}
                                            className="carousel-image max-w-[90%] max-h-[90%] w-auto h-auto object-contain transition-all duration-500 transform hover:scale-105 drop-shadow-2xl"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                            }}
                                        />
                                        {/* Overlay con título */}
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4 text-center">
                                            <h3 className="text-white font-bold text-sm sm:text-base lg:text-lg drop-shadow-lg">
                                                {carouselImages[currentImage].title}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Products Section */}
                <ProductsSection onThemeChange={handleThemeChange} />
                {/* CTA Section */}
                <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-blue-50 to-purple-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Título y descripción */}
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 font-['Dancing_Script'] text-gray-800">
                                {t('🎯 ¿Por qué elegir Princonser Kids?', '🎯 Why choose Princonser Kids?')}
                            </h2>
                            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-5xl mx-auto">
                                {t(
                                    'Princonser Kids es el único sistema que combina filosofía educativa estructurada, valores universales, desarrollo emocional, salud física y financiera, gamificación con recompensas, participación internacional (debates y competencias), plataforma interactiva y auto-implementación familiar (físico + digital con guías incluidas), todo en un ecosistema integral.',
                                    'Princonser Kids is the only system that combines structured educational philosophy, universal values, emotional development, physical and financial health, gamification with rewards, international participation (debates and competitions), interactive platform and family self-implementation (physical + digital with included guides), all in one comprehensive ecosystem.'
                                )}
                            </p>
                        </div>
                        {/* Imagen del cuadro comparativo */}
                        <div className="flex justify-center mb-12">
                            <div className="relative group">
                                <img
                                    src="/images/cuadro.png"
                                    alt="Comparación de metodologías educativas - Princonser Kids vs metodologías tradicionales"
                                    className="max-w-full h-auto rounded-2xl shadow-2xl border-4 border-white transform group-hover:scale-105 transition-all duration-500"
                                />
                            </div>
                        </div>
                        {/* Botón con menú desplegable */}
                        <div className="text-center">
                            <div className="relative inline-block">
                                <button 
                                    onClick={() => {
                                        trackCTAClick('Saber Más', 'CTA Section', 'button');
                                        setIsMenuOpen(!isMenuOpen);
                                    }}
                                    className="px-8 py-4 border-2 border-blue-500 text-blue-500 rounded-full text-lg font-semibold hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 relative"
                                >
                                    {t('Saber Más', 'Learn More')}
                                </button>
                                {/* Menú desplegable */}
                                {isMenuOpen && (
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 py-4 z-50">
                                        <div className="px-4 py-2 text-sm font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-100">
                                            {t('Seleccionar Libro', 'Select Book')}
                                        </div>
                                        <div className="max-h-96 overflow-y-auto">
                                            {[
                                                { id: 2, icon: '👑', label: t('Aprender a Aprender', 'Learning to Learn'), subtitle: t('El Traje Nuevo del Rey', "The King's New Clothes") },
                                                { id: 3, icon: '🐰', label: t('Aprender a Pensar', 'Learning to Think'), subtitle: t('La Liebre y la Tortuga', 'The Hare and the Tortoise') },
                                                { id: 4, icon: '🐭', label: t('Aprender a Debatir', 'Learning to Debate'), subtitle: t('El Ratón de Campo y el Ratón de Ciudad', 'The Town Mouse and the Country Mouse') },
                                                { id: 5, icon: '🦆', label: t('Aprender a Sentir', 'Learning to Feel'), subtitle: t('El Patito Feo', 'The Ugly Duckling') },
                                                { id: 6, icon: '🥣', label: t('Aprender a Interactuar', 'Learning to Interact'), subtitle: t('Sopa de Piedra', 'Stone Soup') },
                                                { id: 7, icon: '🐷', label: t('Aprender a Generar Valor', 'Learning to Generate Value'), subtitle: t('Los Tres Cerditos', 'The Three Little Pigs') },
                                                { id: 8, icon: '🐻', label: t('Aprender a Cuidarse', 'Learning to Take Care'), subtitle: t('Hansel y Gretel', 'Hansel and Gretel') },
                                                { id: 9, icon: '🥚', label: t('Aprender a Financiarse', 'Learning to Finance'), subtitle: t('La Gallina de los Huevos de Oro', 'The Goose That Laid the Golden Eggs') }
                                            ].map((book) => (
                                                <button
                                                    key={book.id}
                                                    onClick={() => {
                                                        trackCTAClick(`Libro ${book.id} - ${book.label}`, 'Book Selection Menu', 'menu-item');
                                                        handleThemeChange(book.id);
                                                        setIsMenuOpen(false);
                                                    }}
                                                    className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-blue-50/80 transition-colors duration-200 border-b border-gray-50 last:border-b-0"
                                                >
                                                    <span className="text-xl">{book.icon}</span>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800 text-sm">{book.label}</div>
                                                        <div className="text-xs text-gray-500">{book.subtitle}</div>
                                                    </div>
                                                    <span className="text-blue-500 text-lg">→</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {/* Overlay para cerrar el menú al hacer clic fuera */}
                                {isMenuOpen && (
                                    <div 
                                        className="fixed inset-0 z-40" 
                                        onClick={() => setIsMenuOpen(false)}
                                    ></div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Home; 